import {
  IncorporationApiData,
  IncorporationApiHistoryEntry,
  IncorporationApiPendency,
  IncorporationApiStep
} from '../../services/IncorporationService';
import {
  INCORPORATION_STAGES,
  IncorporationHistoryEntry,
  IncorporationHistoryType,
  IncorporationStageKey,
  IncorporationStageStatus,
  IncorporationTracking
} from './types';

const STAGE_KEYS = new Set<string>(INCORPORATION_STAGES);

const isStageKey = (value: unknown): value is IncorporationStageKey =>
  typeof value === 'string' && STAGE_KEYS.has(value);

const isStatus = (value: unknown): value is IncorporationStageStatus =>
  value === 'completed' || value === 'in_progress' || value === 'waiting_customer' || value === 'not_started';

const stageIndex = (key: string) => INCORPORATION_STAGES.indexOf(key as IncorporationStageKey);

const mapHistoryEntry = (entry: IncorporationApiHistoryEntry, index: number): IncorporationHistoryEntry | null => {
  const details = entry.details ?? {};
  let type: IncorporationHistoryType;
  let stageKey: IncorporationStageKey | null = null;
  let note: string | undefined;

  if (entry.action === 'step_advanced') {
    type = 'stage_advanced';
    stageKey = isStageKey(details.to) ? details.to : isStageKey(details.from) ? details.from : null;
  } else if (entry.action === 'pendency_opened') {
    type = 'pendency_opened';
    stageKey = isStageKey(details.step) ? details.step : null;
    note = details.description?.trim() || undefined;
  } else if (entry.action === 'document_sent') {
    type = 'document_sent';
    stageKey = isStageKey(details.step) ? details.step : null;
    note = details.original_name?.trim() || undefined;
  } else if (entry.action === 'migrated_from_legacy') {
    type = 'migrated_from_legacy';
    stageKey = isStageKey(details.inferred_step) ? details.inferred_step : null;
  } else {
    return null;
  }

  if (!stageKey) return null;

  return {
    id: `${entry.created_at}-${entry.action}-${index}`,
    at: entry.created_at,
    by: entry.performed_by,
    type,
    stageKey,
    note
  };
};

export const mapIncorporationTracking = (companyId: string, data: IncorporationApiData): IncorporationTracking => {
  const stages = [...(data.steps ?? [])]
    .filter((step): step is IncorporationApiStep & { key: IncorporationStageKey } => isStageKey(step.key))
    .sort((left, right) => (left.order ?? stageIndex(left.key)) - (right.order ?? stageIndex(right.key)))
    .map((step) => ({
      key: step.key,
      status: isStatus(step.status) ? step.status : 'not_started',
      completedAt: step.completed_at
    }));

  const pendencies = (data.pendencies ?? [])
    .filter((item) => item.status === 'open' || !item.resolved_at)
    .filter((item): item is IncorporationApiPendency & { step: IncorporationStageKey } => isStageKey(item.step))
    .map((item) => ({
      id: item.id,
      stageKey: item.step,
      description: item.description,
      createdAt: item.created_at,
      resolvedAt: item.resolved_at
    }));

  const history = (data.history ?? [])
    .map(mapHistoryEntry)
    .filter((entry): entry is IncorporationHistoryEntry => Boolean(entry))
    .sort((left, right) => new Date(right.at).getTime() - new Date(left.at).getTime());

  return {
    companyId,
    jurisdiction: data.jurisdiction?.name || data.jurisdiction?.slug || '',
    currentStep: isStageKey(data.current_step) ? data.current_step : null,
    finishedAt: data.completed_at,
    stages,
    pendencies,
    history
  };
};
